<template>
  <main class="indicator-page">
    <header class="indicator-hero">
      <p>A-Share Market Cap / GDP</p>
      <h1>A 股总市值与 GDP 比值</h1>
      <span>沪市 + 深市 + 北交所总市值 ÷ 上一年中国 GDP</span>
    </header>

    <section class="indicator-shell">
      <div class="toolbar">
        <div>
          <strong>每日收盘后更新</strong>
          <span v-if="latest">最新交易日 {{ latest.trade_date }}</span>
          <span v-if="availableRange.start">
            可用范围 {{ availableRange.start }} 至 {{ availableRange.end }} · {{ availableRange.total_points }} 个数据点
          </span>
        </div>
        <label>
          <span>显示范围</span>
          <select v-model="rangePreset" @change="applyPreset">
            <option value="all">全部历史</option>
            <option value="1y">最近 1 年</option>
            <option value="5y">最近 5 年</option>
            <option value="10y">最近 10 年</option>
            <option value="custom">自定义日期</option>
          </select>
        </label>
        <label>
          <span>开始日期</span>
          <input
            type="date"
            v-model="startDate"
            :min="availableRange.start"
            :max="endDate || availableRange.end"
            @change="rangePreset = 'custom'"
          >
        </label>
        <label>
          <span>结束日期</span>
          <input
            type="date"
            v-model="endDate"
            :min="startDate || availableRange.start"
            :max="availableRange.end"
            @change="rangePreset = 'custom'"
          >
        </label>
        <button type="button" @click="loadData" :disabled="loading">
          {{ loading ? '加载中…' : '应用筛选' }}
        </button>
      </div>

      <div v-if="error" class="state error">{{ error }}</div>
      <div v-else-if="loading && !latest" class="state">数据加载中…</div>

      <template v-if="latest">
        <section class="metric-grid">
          <article class="metric ratio-card" :class="'verdict-' + latest.verdict">
            <span>总市值 / 上年 GDP</span>
            <strong>{{ formatPercent(latest.ratio_pct) }}</strong>
            <em>{{ bandLabel }} · 交易所官方口径</em>
          </article>
          <article class="metric">
            <span>A 股总市值</span>
            <strong>{{ formatTrillion(latest.total_mktcap_100m) }}</strong>
            <em>沪深北交易所口径</em>
          </article>
          <article class="metric">
            <span>{{ latest.gdp_year }} 年 GDP</span>
            <strong>{{ formatTrillion(latest.gdp_100m) }}</strong>
            <em>国家统计局现价口径</em>
          </article>
          <article class="metric">
            <span>统计范围</span>
            <strong>{{ latest.stock_count }} 只</strong>
            <em>采集于 {{ latest.captured_at }}</em>
          </article>
        </section>

        <section class="chart-card">
          <div class="section-heading">
            <div>
              <p>Daily Series</p>
              <h2>总市值、GDP 比值与涨跌率</h2>
            </div>
            <span>官方可核验口径 · {{ history.length }} 个数据点</span>
          </div>
          <div ref="historyChart" class="history-chart" role="img" aria-label="A股总市值、上年GDP比值与较前一数据涨跌率走势图"></div>
        </section>

        <section class="history-card">
          <div class="section-heading">
            <div>
              <p>Official Records</p>
              <h2>官方历史数据</h2>
            </div>
          </div>
          <div class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>交易日</th>
                  <th>频率</th>
                  <th>A 股总市值</th>
                  <th>较前一数据</th>
                  <th>上年 GDP</th>
                  <th>比值</th>
                  <th>来源</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in pagedHistory" :key="row.trade_date">
                  <td>{{ row.trade_date }}</td>
                  <td>{{ row.frequency_label || '交易日' }}</td>
                  <td>{{ formatTrillion(row.total_mktcap_100m) }}</td>
                  <td :class="changeClass(row.market_cap_change_pct)">
                    {{ formatSignedPercent(row.market_cap_change_pct) }}
                  </td>
                  <td>{{ row.gdp_year }} · {{ formatTrillion(row.gdp_100m) }}</td>
                  <td class="ratio-value">{{ formatPercent(row.ratio_pct) }}</td>
                  <td><a v-if="row.source_url" :href="row.source_url" target="_blank" rel="noopener">{{ row.source_name }}</a><span v-else>{{ row.source_name || '交易所' }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="pagination" v-if="totalPages > 1">
            <button type="button" @click="tablePage -= 1" :disabled="tablePage <= 1">上一页</button>
            <span>第 {{ tablePage }} / {{ totalPages }} 页</span>
            <button type="button" @click="tablePage += 1" :disabled="tablePage >= totalPages">下一页</button>
          </div>
        </section>

        <section class="method-card">
          <h2>统计口径</h2>
          <p>
            顶部最新值按上市地统计：上交所、深交所“市场总貌”股票总市值加北交所股票总市值。
            长周期图中 1990—2007 年采用交易所/证监会官方年度年末值；从
            {{ availableRange.daily_start || '2008-01-02' }} 起逐交易日读取上交所、深交所官方每日概况并加总，
            2021-11-15 北交所开市后再加入北交所官方日报。交易所未公开完整日值的早期年份保持年度频率，
            不使用第三方估算补齐。
            GDP 始终取数据日期上一自然年度的现价国内生产总值。
          </p>
          <div class="source-links">
            <a href="https://www.sse.com.cn/market/view/" target="_blank" rel="noopener">上海证券交易所</a>
            <a href="https://www.szse.cn/market/overview/index.html" target="_blank" rel="noopener">深圳证券交易所</a>
            <a href="https://www.bse.cn/market/market_data/market_overview.html" target="_blank" rel="noopener">北京证券交易所</a>
            <a :href="gdpSourceUrl" target="_blank" rel="noopener">国家统计局 GDP 来源</a>
          </div>
          <p class="disclaimer">该指标用于观察证券化程度，不构成投资建议，也不能单独代表市场高估或低估。</p>
        </section>
      </template>
    </section>
  </main>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'a-share-market-cap-gdp',
  data: function () {
    return {
      rangePreset: 'all',
      startDate: '',
      endDate: '',
      availableRange: {},
      latest: null,
      history: [],
      tablePage: 1,
      pageSize: 50,
      loading: false,
      error: '',
      chart: null
    }
  },
  computed: {
    descendingHistory: function () {
      return this.history.slice().reverse()
    },
    totalPages: function () {
      return Math.max(1, Math.ceil(this.descendingHistory.length / this.pageSize))
    },
    pagedHistory: function () {
      var start = (this.tablePage - 1) * this.pageSize
      return this.descendingHistory.slice(start, start + this.pageSize)
    },
    bandLabel: function () {
      return (this.latest && this.latest.detail && this.latest.detail.band) || '市场证券化率'
    },
    gdpSourceUrl: function () {
      return (
        this.latest &&
        this.latest.detail &&
        this.latest.detail.gdp_source_url
      ) || 'https://www.stats.gov.cn/sj/tjgb/ndtjgb/qgndtjgb/'
    }
  },
  mounted: function () {
    this.loadData()
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy: function () {
    window.removeEventListener('resize', this.resizeChart)
    if (this.chart) this.chart.dispose()
  },
  methods: {
    applyPreset: function () {
      if (this.rangePreset === 'custom') return
      if (this.rangePreset === 'all') {
        this.startDate = ''
        this.endDate = ''
        this.loadData()
        return
      }
      var endText = this.availableRange.end || new Date().toISOString().slice(0, 10)
      var end = new Date(endText + 'T00:00:00')
      var years = this.rangePreset === '1y' ? 1 : (this.rangePreset === '5y' ? 5 : 10)
      var start = new Date(end.getTime())
      start.setFullYear(start.getFullYear() - years)
      this.startDate = this.toISODate(start)
      this.endDate = endText
      this.loadData()
    },
    loadData: function () {
      var self = this
      self.loading = true
      self.error = ''
      var query = this.rangePreset === 'all'
        ? 'range=all'
        : 'start_date=' + encodeURIComponent(this.startDate) + '&end_date=' + encodeURIComponent(this.endDate)
      var reqUrl = this.$store.state.baseUrl + '/blog/api/market/buffett/?' + query
      this.$http.get(reqUrl).then(function (response) {
        var result = JSON.parse(response.bodyText)
        if (result.result !== 'success') {
          throw new Error(result.message || '数据加载失败')
        }
        self.latest = result.latest
        self.history = result.history || []
        self.availableRange = result.available_range || {}
        self.tablePage = 1
        self.loading = false
        self.$nextTick(self.renderChart)
      }).catch(function (error) {
        self.loading = false
        self.error = error.message || 'A 股总市值数据加载失败'
      })
    },
    renderChart: function () {
      if (!this.$refs.historyChart) return
      if (!this.chart) this.chart = echarts.init(this.$refs.historyChart)
      var dates = this.history.map(function (row) { return row.trade_date })
      var ratios = this.history.map(function (row) { return row.ratio_pct })
      var caps = this.history.map(function (row) {
        return Number((row.total_mktcap_100m / 10000).toFixed(4))
      })
      var changes = this.history.map(function (row) {
        return row.market_cap_change_pct == null ? null : Number(row.market_cap_change_pct.toFixed(2))
      })
      var rows = this.history
      this.chart.setOption({
        animationDuration: 450,
        color: ['#1f6feb', '#d97706', '#64748b'],
        tooltip: {
          trigger: 'axis',
          formatter: function (items) {
            if (!items || !items.length) return ''
            var lines = [items[0].axisValue]
            items.forEach(function (item) {
              var unit = item.seriesName === 'A 股总市值' ? ' 万亿元' : '%'
              lines.push(
                item.marker + item.seriesName + '：' +
                (item.value == null ? '—' : item.value + unit)
              )
            })
            var row = rows[items[0].dataIndex]
            if (row) {
              lines.push('频率：' + (row.frequency_label || '交易日'))
              lines.push('来源：' + (row.source_name || '交易所'))
            }
            return lines.join('<br>')
          }
        },
        legend: {
          top: 4,
          data: ['总市值 / GDP', 'A 股总市值', '较前一数据涨跌率']
        },
        grid: {
          left: 20,
          right: 92,
          top: 50,
          bottom: 68,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: dates,
          axisLabel: { hideOverlap: true }
        },
        yAxis: [
          {
            type: 'value',
            name: '比值 %',
            scale: true,
            axisLabel: { formatter: '{value}%' },
            splitLine: { lineStyle: { color: '#edf1f5' } }
          },
          {
            type: 'value',
            name: '万亿元',
            scale: true,
            axisLabel: { formatter: '{value}' },
            splitLine: { show: false }
          },
          {
            type: 'value',
            name: '涨跌 %',
            position: 'right',
            offset: 58,
            scale: true,
            axisLabel: { formatter: '{value}%' },
            splitLine: { show: false }
          }
        ],
        dataZoom: [
          { type: 'inside', start: 0, end: 100 },
          { type: 'slider', start: 0, end: 100, height: 20, bottom: 16 }
        ],
        series: [
          {
            name: '总市值 / GDP',
            type: 'line',
            data: ratios,
            smooth: 0.2,
            showSymbol: ratios.length < 80,
            symbolSize: 5,
            lineStyle: { width: 3 },
            areaStyle: { opacity: 0.08 }
          },
          {
            name: 'A 股总市值',
            type: 'line',
            yAxisIndex: 1,
            data: caps,
            smooth: 0.2,
            showSymbol: caps.length < 80,
            symbolSize: 5,
            lineStyle: { width: 2, type: 'dashed' }
          },
          {
            name: '较前一数据涨跌率',
            type: 'bar',
            yAxisIndex: 2,
            data: changes,
            barMaxWidth: 6,
            itemStyle: {
              color: function (params) {
                if (params.value == null) return '#94a3b8'
                return params.value >= 0 ? '#d14a3a' : '#16835b'
              }
            }
          }
        ]
      }, true)
    },
    resizeChart: function () {
      if (this.chart) this.chart.resize()
    },
    formatTrillion: function (value) {
      if (value == null) return '—'
      var number = Number(value)
      if (number < 10000) return number.toFixed(2) + ' 亿元'
      return (number / 10000).toFixed(2) + ' 万亿元'
    },
    formatPercent: function (value) {
      if (value == null) return '—'
      return Number(value).toFixed(2) + '%'
    },
    formatSignedPercent: function (value) {
      if (value == null) return '—'
      var number = Number(value)
      return (number > 0 ? '+' : '') + number.toFixed(2) + '%'
    },
    changeClass: function (value) {
      if (value == null || Number(value) === 0) return 'change-flat'
      return Number(value) > 0 ? 'change-up' : 'change-down'
    },
    toISODate: function (date) {
      var year = date.getFullYear()
      var month = String(date.getMonth() + 1).padStart(2, '0')
      var day = String(date.getDate()).padStart(2, '0')
      return year + '-' + month + '-' + day
    }
  }
}
</script>

<style scoped>
.indicator-page {
  min-height: 100vh;
  background: #f3f6fa;
  color: #172033;
}

.indicator-hero {
  border-bottom: 1px solid #e1e7ee;
  padding: 48px 20px 44px;
  background: linear-gradient(135deg, #ffffff 0%, #f1f6fc 100%);
  text-align: center;
}

.indicator-hero p,
.section-heading p {
  margin: 0 0 7px;
  color: #52709a;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.indicator-hero h1 {
  margin: 0;
  color: #101828;
  font-size: 38px;
  line-height: 1.2;
}

.indicator-hero span {
  display: block;
  margin-top: 12px;
  color: #667085;
  font-size: 16px;
}

.indicator-shell {
  width: min(1180px, calc(100% - 40px));
  margin: 0 auto;
  padding: 28px 0 72px;
}

.toolbar,
.metric,
.chart-card,
.history-card,
.method-card {
  border: 1px solid #dce4ed;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(16, 24, 40, .04);
}

.toolbar {
  display: flex;
  gap: 16px;
  align-items: end;
  justify-content: flex-end;
  padding: 16px 18px;
}

.toolbar > div {
  display: grid;
  gap: 4px;
  margin-right: auto;
}

.toolbar > div span,
.toolbar label span {
  color: #667085;
  font-size: 12px;
}

.toolbar label {
  display: grid;
  gap: 5px;
}

.toolbar select,
.toolbar input,
.toolbar button {
  min-height: 40px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px 12px;
  background: #fff;
  color: #263445;
  font-size: 14px;
}

.toolbar button {
  border-color: #1f6feb;
  background: #1f6feb;
  color: #fff;
  cursor: pointer;
}

.toolbar button:disabled {
  cursor: wait;
  opacity: .6;
}

.state {
  padding: 24px 2px;
  color: #667085;
}

.state.error {
  color: #b42318;
}

.metric-grid {
  display: grid;
  grid-template-columns: 1.15fr repeat(3, 1fr);
  gap: 14px;
  margin-top: 18px;
}

.metric {
  min-height: 112px;
  padding: 18px;
}

.metric span,
.metric em {
  color: #667085;
  font-size: 12px;
  font-style: normal;
}

.metric strong {
  display: block;
  margin: 12px 0 8px;
  color: #101828;
  font-size: 24px;
}

.ratio-card strong {
  font-size: 38px;
}

.ratio-card.verdict-undervalued strong { color: #16833a; }
.ratio-card.verdict-neutral strong { color: #52606d; }
.ratio-card.verdict-overvalued strong { color: #c2413b; }

.chart-card,
.history-card,
.method-card {
  margin-top: 18px;
}

.section-heading {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 14px;
  padding: 20px 22px 0;
}

.section-heading h2,
.method-card h2 {
  margin: 0;
  color: #101828;
  font-size: 22px;
}

.section-heading > span {
  color: #667085;
  font-size: 13px;
}

.history-chart {
  width: 100%;
  height: 420px;
}

.table-wrap {
  overflow-x: auto;
  padding: 16px 22px 22px;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

th,
td {
  border-bottom: 1px solid #e8edf2;
  padding: 11px 10px;
  text-align: right;
  white-space: nowrap;
}

th {
  color: #667085;
  font-weight: 700;
}

th:first-child,
td:first-child {
  text-align: left;
}

.ratio-value {
  color: #1f5fae;
  font-weight: 700;
}

.change-up { color: #c2413b; font-weight: 700; }
.change-down { color: #16835b; font-weight: 700; }
.change-flat { color: #667085; }

td small {
  display: block;
  color: #b42318;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 0 22px 22px;
  color: #667085;
  font-size: 13px;
}

.pagination button {
  border: 1px solid #cbd5e1;
  border-radius: 7px;
  padding: 7px 12px;
  background: #fff;
  color: #263445;
  cursor: pointer;
}

.pagination button:disabled {
  cursor: not-allowed;
  opacity: .45;
}

.method-card {
  padding: 22px;
}

.method-card p {
  margin: 12px 0 0;
  color: #52606d;
  line-height: 1.75;
}

.source-links {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
  margin-top: 14px;
}

.source-links a {
  color: #1f6feb;
}

.method-card .disclaimer {
  color: #8a5b12;
  font-size: 12px;
}

@media (max-width: 900px) {
  .indicator-shell {
    width: min(100% - 28px, 720px);
  }

  .metric-grid {
    grid-template-columns: 1fr 1fr;
  }

  .toolbar {
    align-items: stretch;
    flex-wrap: wrap;
  }

  .toolbar > div {
    flex-basis: 100%;
  }
}

@media (max-width: 560px) {
  .indicator-hero h1 {
    font-size: 30px;
  }

  .metric-grid {
    grid-template-columns: 1fr;
  }

  .history-chart {
    height: 340px;
  }

  .toolbar label,
  .toolbar select,
  .toolbar input,
  .toolbar button {
    width: 100%;
  }
}
</style>
